import { useGetStateItem } from './casper-service-hooks';
import {
  findHash,
  isValidAccountHash,
  isValidCsprName,
  isValidPublicKey,
} from '../utils/account';
import {
  ApiGetResult,
  useGetAccountInfo,
  useGetBlock,
  useGetCsprNameInfo,
  useGetDeploy,
} from './event-store-hooks';
import { hashPrefixRegEx, NamedKeyPrefix } from '@make-software/cspr-ui';
import { ContractDataRawResult } from '@block-explorer/entities';
import { checkApiHookIsLoading } from './api-hooks-utils';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { useGetCasperNetwork } from '../services/casper-network-context';

/** SEARCH */

type SearchResult = {
  type:
    | 'block'
    | 'deploy'
    | 'transaction'
    | 'account'
    | 'contract'
    | 'contract-package'
    | 'uref';
  hash: string;
};
/**
 * @usage
 * ```
 * const { data: { type, hash } } = useSearch('hash');
 * ```
 * @todo consider moving out of event-store-utils
 */
export const useSearch = (
  searchTerm: string | null
): Omit<ApiGetResult<SearchResult>, 'reloadData'> => {
  const { networkMajorVersion } = useGetCasperNetwork();
  const searchTermLength = searchTerm?.trim().length;

  const { selectedCurrency: currency } = useSelector(
    (state: RootState) => state.currencies
  );

  const searchTermWithPrefix =
    searchTerm && searchTermLength === 64
      ? NamedKeyPrefix.HASH + searchTerm
      : searchTerm;

  const {
    data: stateItemData,
    error: stateItemError,
    loading: stateItemLoading,
  } = useGetStateItem<ContractDataRawResult | null>(
    searchTermWithPrefix && findHash(searchTermWithPrefix)
      ? {
          key: searchTermWithPrefix,
        }
      : null
  );

  const { data: accountPublicKey, loading: accountPublicKeyLoading } =
    useGetAccountInfo(
      searchTerm && searchTermLength === 64 ? { accountHash: searchTerm } : null
    );

  const {
    data: blockData,
    error: blockError,
    loading: blockLoading,
  } = useGetBlock(searchTerm && searchTermLength === 64 ? searchTerm : null);

  const {
    data: deployData,
    error: deployError,
    loading: deployLoading,
  } = useGetDeploy(searchTerm && searchTermLength === 64 ? searchTerm : null, {
    includes: `rate(${currency.id}),contract_entrypoint,contract_package,transfers,entry_point_id,nft_token_actions,ft_token_actions`,
  });

  const {
    data: csprNameInfo,
    loading: csprNameInfoLoading,
    error: csprNameInfoError,
  } = useGetCsprNameInfo(
    searchTerm && isValidCsprName(searchTerm)
      ? {
          name: searchTerm,
          includes: 'resolved_public_key,account_info,centralized_account_info',
        }
      : null
  );

  if (searchTerm == null) {
    return { loading: false, error: null, data: null };
  }
  const [searchTermWithoutPrefix] = searchTerm
    .replace(hashPrefixRegEx, '')
    .split('-');

  const isValidAccount = isValidPublicKey(searchTerm);
  const isAccountHashValid = isValidAccountHash(searchTerm);

  const isValidSearchTermLength =
    (searchTermWithoutPrefix && searchTermWithoutPrefix.length === 64) ||
    isValidCsprName(searchTerm) ||
    isValidAccount;

  if (!isValidSearchTermLength) {
    return { loading: false, error: 'Search could not complete.', data: null };
  }

  const deployHookLoading = checkApiHookIsLoading({
    loading: deployLoading,
    error: deployError,
  });

  const blockHookLoading = checkApiHookIsLoading({
    loading: blockLoading,
    error: blockError,
  });

  const accountHookLoading = checkApiHookIsLoading({
    loading: accountPublicKeyLoading,
    error: accountPublicKey?.error?.message || null,
  });

  const csprNameInfoHookLoading = checkApiHookIsLoading({
    loading: csprNameInfoLoading,
    error: csprNameInfoError,
  });

  const stateHookLoading = checkApiHookIsLoading({
    loading: stateItemLoading,
    error: stateItemError,
  });

  if (
    !isValidAccount &&
    !isValidCsprName(searchTerm) && // workaround for account loading case
    (deployHookLoading ||
      blockHookLoading ||
      accountHookLoading ||
      csprNameInfoHookLoading ||
      stateHookLoading)
  ) {
    return { loading: true, error: null, data: null };
  }

  const loadedData =
    deployData || blockData || stateItemData || csprNameInfo?.data;

  if (
    !isValidAccount &&
    !isAccountHashValid &&
    !loadedData &&
    (csprNameInfoError ||
      deployError ||
      blockError ||
      stateItemError ||
      accountPublicKey?.error)
  ) {
    return {
      loading: false,
      error: 'Search could not complete.',
      data: null,
    };
  }

  const response: SearchResult | null =
    csprNameInfo != null
      ? {
          type: 'account',
          hash:
            csprNameInfo.data.resolved_public_key ||
            csprNameInfo.data.resolved_hash,
        }
      : blockData != null
        ? {
            type: 'block',
            hash: searchTerm,
          }
        : deployData != null
          ? {
              type: networkMajorVersion === '2' ? 'transaction' : 'deploy',
              hash: searchTerm,
            }
          : accountPublicKey != null && !!accountPublicKey.data.public_key
            ? {
                type: 'account',
                hash: accountPublicKey.data.public_key,
              }
            : stateItemData?.Contract != null
              ? {
                  type: 'contract',
                  hash: searchTerm.replace(NamedKeyPrefix.HASH, ''),
                }
              : stateItemData?.ContractPackage != null
                ? {
                    type: 'contract-package',
                    hash: searchTerm.replace(NamedKeyPrefix.HASH, ''),
                  }
                : stateItemData != null
                  ? {
                      type: 'uref',
                      hash: searchTerm,
                    }
                  : isValidAccount || isAccountHashValid
                    ? {
                        type: 'account',
                        hash: searchTerm,
                      }
                    : null;

  return { loading: false, error: null, data: response };
};
